import { useState, useCallback, useRef, useEffect } from 'react';

export interface FlashMessage {
  type: 'success' | 'error';
  content: string;
  id: string;
}

export interface UseFlashMessagesOptions {
  autoDismissTimeout?: number; // Default: 4000ms
  enableScreenReader?: boolean; // Default: true
}

export interface UseFlashMessagesReturn {
  flashMessages: FlashMessage[];
  addFlashMessage: (type: 'success' | 'error', content: string) => void;
  removeFlashMessage: (id: string) => void;
  announceMessage?: string; // Only present if enableScreenReader is true
}

export const useFlashMessages = (options: UseFlashMessagesOptions = {}): UseFlashMessagesReturn => {
  const { autoDismissTimeout = 4000, enableScreenReader = true } = options;

  const [flashMessages, setFlashMessages] = useState<FlashMessage[]>([]);
  const [announceMessage, setAnnounceMessage] = useState<string>('');
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup: Clear all timeouts when component unmounts
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeoutRefs.current.clear();
    };
  }, []);

  // Add flash message
  const addFlashMessage = useCallback(
    (type: 'success' | 'error', content: string) => {
      const id = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const message: FlashMessage = { type, content, id };

      setFlashMessages((prev) => [...prev, message]);

      // Announce to screen readers if enabled
      if (enableScreenReader) {
        setAnnounceMessage(content);
        setTimeout(() => setAnnounceMessage(''), 100);
      }

      // Auto-remove message after timeout
      const timeoutId = setTimeout(() => {
        setFlashMessages((prev) => prev.filter((msg) => msg.id !== id));
        timeoutRefs.current.delete(id);
      }, autoDismissTimeout);

      // Store timeout ID for cleanup
      timeoutRefs.current.set(id, timeoutId);
    },
    [autoDismissTimeout, enableScreenReader]
  );

  // Remove flash message
  const removeFlashMessage = useCallback((id: string) => {
    // Clear timeout if it exists
    const timeoutId = timeoutRefs.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefs.current.delete(id);
    }

    setFlashMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  const returnValue: UseFlashMessagesReturn = {
    flashMessages,
    addFlashMessage,
    removeFlashMessage
  };

  // Only include announceMessage if screen reader is enabled
  if (enableScreenReader) {
    returnValue.announceMessage = announceMessage;
  }

  return returnValue;
};
